import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/searchbar";
import {
  openPuckDrawer,
} from "@/components/cms-puck/fields/puck-drawer-store";
import type { SelectionItem } from "@/lib/cms-puck/blocks/selection";
import { cmsNew, useCmsNew } from "@/lib/cms/cms-new-i18n";
import {
  fetchCatalogItemsByIds,
  fetchCatalogList,
} from "@/api/cms-puck/catalog-picker-api";

const KEY = (it: SelectionItem) => `${it.type}:${it.id}`;

function ExhibitionPickerBody({
  selected,
  onToggle,
}: {
  selected: SelectionItem[];
  onToggle: (item: SelectionItem) => void;
}) {
  const [search, setSearch] = useState("");
  const { data: list = [], isLoading } = useQuery({
    queryKey: ["puck-exhibitions", search],
    queryFn: () => fetchCatalogList("exhibition", search),
    placeholderData: keepPreviousData,
  });
  const selectedKeys = new Set(selected.map(KEY));

  return (
    <div className="flex h-full flex-col gap-3 pt-4">
      <SearchBar
        search={search}
        onChangeSearch={setSearch}
        placeholder={cmsNew("cms.new.auto.placeholder.rechercher_une_exposition", "Rechercher une exposition…")}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="text-sm text-slate-400">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-slate-400">{cmsNew("cms.new.auto.text.aucune_exposition_publiee", "Aucune exposition publiée.")}</p>
        ) : (
          <ul className="space-y-1">
            {list.map((row) => {
              const item: SelectionItem = { type: "exhibition", id: row.id };
              const active = selectedKeys.has(KEY(item));
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => onToggle(item)}
                    className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                      active ? "bg-slate-900 text-white" : "hover:bg-slate-100"
                    }`}
                  >
                    {row.title}
                    {row.registrationNumber ? (
                      <span className="ml-2 text-xs opacity-70">
                        {row.registrationNumber}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Picker multi-expositions (bloc ExhibitionList). */
export function ExhibitionListPickerField({
  value,
  onChange,
}: {
  value?: SelectionItem[];
  onChange: (items: SelectionItem[]) => void;
}) {
  const cmsNew = useCmsNew();
  const items = (value ?? []).filter((it) => it.type === "exhibition" && it.id);
  const { data: resolved = [] } = useQuery({
    queryKey: ["puck-exhibitions-by-ids", items.map((i) => i.id)],
    queryFn: () =>
      fetchCatalogItemsByIds(
        "exhibition",
        items.map((i) => i.id),
      ),
    enabled: items.length > 0,
  });
  const titleById = new Map(resolved.map((r) => [r.id, r.title]));

  const toggle = (item: SelectionItem) => {
    const k = KEY(item);
    if (items.some((it) => KEY(it) === k)) {
      onChange(items.filter((it) => KEY(it) !== k));
    } else {
      onChange([...items, item]);
    }
  };

  const open = () => {
    openPuckDrawer({
      title: cmsNew("cms.new.auto.title.selectionner_des_expositions", "Sélectionner des expositions"),
      maxWidth: "sm:max-w-md",
      node: (
        <ExhibitionPickerBody
          selected={items}
          onToggle={(item) => {
            toggle(item);
          }}
        />
      ),
    });
  };

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">{cmsNew("cms.new.auto.text.aucune_exposition_selectionnee", "Aucune exposition sélectionnée")}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li
              key={KEY(it)}
              className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm"
            >
              <span>{titleById.get(it.id) ?? it.id}</span>
              <button
                type="button"
                onClick={() => toggle(it)}
                className="text-slate-400 hover:text-slate-600"
                aria-label={cmsNew("cms.new.auto.label.retirer", "Retirer")}
              >
                <XIcon className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Button type="button" variant="outline" size="sm" onClick={open}>
        Ajouter / modifier
      </Button>
    </div>
  );
}
